import React from 'react';
import { ThemeContext } from 'styled-components';

// Resolve a theme TOKEN to the colour it currently wears.
//
// This exists because of a gap, not a preference: the @ui text components take a raw colour, and
// format.js — which is where the token is decided — has no theme access. So every surface that
// wants "the colour for a negative change" reaches into the raw theme object itself. Three
// components had grown their own identical resolver, none of them greppable as a styled-component,
// and the positive/negative pair is the most likely thing a palette pass touches.
//
// The optional chaining is load-bearing pessimism, kept from the copies it replaces: with no
// ThemeProvider mounted the lookup yields `undefined` rather than throwing, and `undefined` handed
// to Body5's `color` leaves it on the ordinary text colour. That is the right failure — a score
// direction that renders uncoloured understates, where a thrown error takes the table with it.
//
// Returns a resolver rather than a colour so a component can call it per row without a hook in a
// loop. A falsy token yields `undefined`, so callers can pass `scoreDeltaToken(delta)` straight in
// without guarding the flat-or-absent case.
//
// This is a stopgap at the feature level. The real fix is a token-aware colour on the text layer
// itself — `<Body5 $token="negative">` resolved inside BaseText — at which point this hook and all
// three of its call sites go away.
export const useThemeToken = () => {
  const theme = React.useContext(ThemeContext);
  return React.useCallback((token) => (token && theme?.themeColors?.[token]) || undefined, [theme]);
};
